// FIS Prepaid ACC (Automated Client Configuration) — Quick Edit recipe library.
// Frontend-only: each recipe declares its form fields (drives rendering + validation)
// and a build() that returns an ordered XML node tree. No backend calls.
//
// Source of truth: Prepaid ACC Specification, User Guide v1.26 (Aug 27, 2024).
// Element order within a node matters (per Appendix B XSD), so build() returns
// rows in schema order; empty optional rows are dropped by the serializer.

// Characters that ABORT the batch loader (spec §1.5.1). Block, never escape.
export const ILLEGAL_CHARS = /[&<>"#']/;
export const PURSE_NAME_RE = /^[A-Z]{3}[0-9]{4}$/; // e.g. FSA2026

// ---- Serializer ----------------------------------------------------------
// row = ['Tag','value']  (dropped when value is '' / null)
//     | { node:'Name', rows:[...] }   (nested element)
function renderNode(name, rows, indent) {
  const pad = '  '.repeat(indent);
  let out = `${pad}<${name}>\n`;
  for (const r of rows) {
    if (Array.isArray(r)) {
      const [tag, val] = r;
      if (val === '' || val == null) continue;
      out += `${'  '.repeat(indent + 1)}<${tag}>${val}</${tag}>\n`;
    } else if (r && r.node) {
      out += renderNode(r.node, r.rows, indent + 1);
    }
  }
  out += `${pad}</${name}>\n`;
  return out;
}

export function renderItem(item, indent = 1) {
  return renderNode(item.node, item.rows, indent);
}

export function buildBatch(businessPartner, items) {
  let xml = '<?xml version="1.0" encoding="utf-8"?>\n';
  xml += `<Batch BusinessPartner="${businessPartner || 'Business Partner Name'}">\n`;
  for (const it of items) xml += renderItem(it, 1);
  xml += '</Batch>\n';
  return xml;
}

// ---- Validation ----------------------------------------------------------
export function fieldVisible(field, values) {
  return typeof field.showIf === 'function' ? field.showIf(values) : true;
}

export function validateField(field, value, values) {
  if (!fieldVisible(field, values)) return null;
  const v = value == null ? '' : String(value);
  const required = typeof field.required === 'function' ? field.required(values) : field.required;
  if (field.type !== 'select' && ILLEGAL_CHARS.test(v)) {
    return 'Illegal character (& < > " # ’) — would abort the batch';
  }
  if (required && v.trim() === '') return 'Required';
  if (v.trim() === '') return null; // optional & empty → ok
  if (field.pattern && !field.pattern.test(v)) return field.patmsg || 'Invalid format';
  if (field.maxLen && v.length > field.maxLen) return `Max ${field.maxLen} characters`;
  if (field.type === 'number') {
    const n = Number(v);
    if (Number.isNaN(n)) return 'Must be a number';
    if (field.min != null && n < field.min) return `Min ${field.min}`;
    if (field.max != null && n > field.max) return `Max ${field.max}`;
  }
  return null;
}

export function validateRecipe(recipe, values) {
  const errors = {};
  for (const f of recipe.fields) {
    const e = validateField(f, values[f.id], values);
    if (e) errors[f.id] = e;
  }
  return errors;
}

export function initialValues(recipe) {
  const v = {};
  for (const f of recipe.fields) v[f.id] = f.default ?? '';
  return v;
}

// ---- Field shorthands ----------------------------------------------------
const SP = { id: 'sp', label: 'Sub_Program Name', type: 'text', required: true, maxLen: 30, help: 'Alpha-numeric. Locates the subprogram.' };
const PN = (required = true) => ({ id: 'pn', label: 'Purse Name', type: 'text', required, pattern: PURSE_NAME_RE, patmsg: '3 letters + 4 digits, e.g. FSA2026', help: 'Internally-recognized purse name.' });
const EXT_FLAGS = ['No Grace Period', 'POS and Batch', 'POS Only', 'Batch Only'];

// ---- Recipes -------------------------------------------------------------
export const RECIPE_GROUPS = [
  'Sub_Program & Purse',
  'Fees, MCCs & Countries',
  'Packages & BPA',
];

export const RECIPES = [
  // ===================== Sub_Program & Purse =====================
  {
    id: 'purse-expiry',
    group: 'Sub_Program & Purse',
    title: 'Change Purse Expiry',
    blurb: 'Move a purse plan-period end date, with optional grace extension.',
    nodeLabel: 'Purses › Purse · Overwrite (PA 3)',
    fields: [
      SP,
      PN(),
      { id: 'ed', label: 'New Effective Date', type: 'date', help: 'Base plan-period start (optional).' },
      { id: 'xd', label: 'New Expiry Date (ExpDate)', type: 'date', required: true },
      { id: 'gx', label: 'Grace / Extension', type: 'select', options: EXT_FLAGS, default: 'No Grace Period' },
      { id: 'days', label: 'Extension Period (days)', type: 'number', min: 1, max: 80, required: (v) => v.gx !== 'No Grace Period', showIf: (v) => v.gx !== 'No Grace Period' },
      { id: 'exdate', label: 'Extension Date', type: 'date', required: (v) => v.gx !== 'No Grace Period', showIf: (v) => v.gx !== 'No Grace Period' },
    ],
    build: (v) => ({ node: 'Purses', rows: [{ node: 'Purse', rows: [
      ['PerformAction', '3'], ['Sub_ProgramName', v.sp], ['PurseName', v.pn],
      ['EffectiveDate', v.ed], ['ExpDate', v.xd],
      ['ExpExtensionFlag', v.gx === 'No Grace Period' ? '' : v.gx],
      ['ExtensionPeriodinDays', v.gx === 'No Grace Period' ? '' : v.days],
      ['ExtensionDate', v.gx === 'No Grace Period' ? '' : v.exdate],
    ] }] }),
  },
  {
    id: 'purse-add',
    group: 'Sub_Program & Purse',
    title: 'Add a Purse',
    blurb: 'Create a new purse on a subprogram with MCC group and limits.',
    nodeLabel: 'Purses › Purse · Add (PA 1)',
    fields: [
      SP, PN(),
      { id: 'desc', label: 'Purse Description', type: 'text', required: true, maxLen: 30 },
      { id: 'ed', label: 'Effective Date', type: 'date', required: true },
      { id: 'xd', label: 'Expiry Date', type: 'date', required: true },
      { id: 'mcc', label: 'MCC Group', type: 'text', required: true, pattern: /^\d{5}$/, patmsg: '5-digit MCC group' },
      { id: 'net', label: 'Network Name', type: 'text', maxLen: 30, help: 'Optional; must already exist.' },
      { id: 'iias', label: 'IIAS Group', type: 'text', pattern: /^\d{5}$/, patmsg: '5-digit IIAS group', help: 'Optional.' },
      { id: 'minv', label: 'Min Value', type: 'number' },
      { id: 'maxv', label: 'Max Value', type: 'number' },
      { id: 'minl', label: 'Min Load', type: 'number' },
      { id: 'maxl', label: 'Max Load', type: 'number' },
    ],
    build: (v) => ({ node: 'Purses', rows: [{ node: 'Purse', rows: [
      ['PerformAction', '1'], ['Sub_ProgramName', v.sp], ['PurseName', v.pn],
      ['PurseDescription', v.desc], ['EffectiveDate', v.ed], ['ExpDate', v.xd],
      ['NetworkName', v.net], ['MCCGroup', v.mcc], ['IIASGroup', v.iias],
      ['MinValue', v.minv], ['MaxValue', v.maxv], ['MinLoad', v.minl], ['MaxLoad', v.maxl],
    ] }] }),
  },
  {
    id: 'purse-priority',
    group: 'Sub_Program & Purse',
    title: 'Reprioritize Purses',
    blurb: 'Set the purse-type priority order used during authorization.',
    nodeLabel: 'Purses › Purse · Overwrite (PA 3)',
    fields: [
      SP, PN(),
      { id: 'list', label: 'Purse Priority List', type: 'text', required: true, maxLen: 2048, help: 'Comma-delimited purse types, e.g. FSA,HSA,FOD,OTC' },
    ],
    build: (v) => ({ node: 'Purses', rows: [{ node: 'Purse', rows: [
      ['PerformAction', '3'], ['Sub_ProgramName', v.sp], ['PurseName', v.pn], ['PursePriorityList', v.list],
    ] }] }),
  },
  {
    id: 'purse-limits',
    group: 'Sub_Program & Purse',
    title: 'Purse Value / Load Limits',
    blurb: 'Adjust min/max value and per-load limits on an existing purse.',
    nodeLabel: 'Purses › Purse · Overwrite (PA 3)',
    fields: [
      SP, PN(),
      { id: 'minv', label: 'Min Value', type: 'number' },
      { id: 'maxv', label: 'Max Value', type: 'number' },
      { id: 'minl', label: 'Min Load', type: 'number' },
      { id: 'maxl', label: 'Max Load', type: 'number' },
    ],
    build: (v) => ({ node: 'Purses', rows: [{ node: 'Purse', rows: [
      ['PerformAction', '3'], ['Sub_ProgramName', v.sp], ['PurseName', v.pn],
      ['MinValue', v.minv], ['MaxValue', v.maxv], ['MinLoad', v.minl], ['MaxLoad', v.maxl],
    ] }] }),
  },
  {
    id: 'purse-cashrule',
    group: 'Sub_Program & Purse',
    title: 'Add Cash / MCC Track',
    blurb: 'Attach a cash track (with velocity) or an MCC track (txn limits) to a purse.',
    nodeLabel: 'Purses › Purse › PurseCashRules',
    fields: [
      SP, PN(),
      { id: 'action', label: 'Action', type: 'select', options: ['Add', 'Delete'], default: 'Add' },
      { id: 'kind', label: 'Track type', type: 'select', options: ['Cash track', 'MCC track'], default: 'Cash track' },
      { id: 'cash', label: 'Cash Type', type: 'select', showIf: (v) => v.kind === 'Cash track', required: (v) => v.kind === 'Cash track',
        options: ['General purchase', 'Financial Institutions :Manual Cash Disbursements', 'Financial Institutions :Automated Cash Disbursements', 'Financial Institutions :International ATM', 'DDA transfer', 'Cashback'], default: 'General purchase' },
      { id: 'mcc', label: 'MCC Group', type: 'text', showIf: (v) => v.kind === 'MCC track', required: (v) => v.kind === 'MCC track', pattern: /^\d{5}$/, patmsg: '5-digit MCC group' },
      { id: 'cycType', label: 'Cycle Type', type: 'select', options: ['Monthly', 'Daily'], default: 'Monthly', showIf: (v) => v.kind === 'Cash track' },
      { id: 'cycReset', label: 'Cycle Reset', type: 'number', min: 0, max: 999, showIf: (v) => v.kind === 'Cash track', help: 'Daily=days; Monthly=day-of-month 1–28.' },
      { id: 'cycMax', label: 'Cycle Spend Max', type: 'number', showIf: (v) => v.kind === 'Cash track' },
      { id: 'txnMin', label: 'Txn Min Amount', type: 'number' },
      { id: 'txnMax', label: 'Txn Max Amount', type: 'number' },
    ],
    build: (v) => ({ node: 'Purses', rows: [{ node: 'Purse', rows: [
      ['PerformAction', '3'], ['Sub_ProgramName', v.sp], ['PurseName', v.pn],
      { node: 'PurseCashRules', rows: [
        ['PerformAction', v.action === 'Delete' ? '5' : '1'],
        ['Sub_ProgramName', v.sp], ['PurseName', v.pn], ['Action', v.action],
        ['CashType', v.kind === 'Cash track' ? v.cash : ''],
        ['MCCGroup', v.kind === 'MCC track' ? v.mcc : ''],
        ['CycleType', v.kind === 'Cash track' ? v.cycType : ''],
        ['CycleReset', v.kind === 'Cash track' ? v.cycReset : ''],
        ['CycleSpendMaxAmount', v.kind === 'Cash track' ? v.cycMax : ''],
        ['TxnSpendMinAmount', v.txnMin], ['TxnSpendMaxAmount', v.txnMax],
      ] },
    ] }] }),
  },
  {
    id: 'default-purse',
    group: 'Sub_Program & Purse',
    title: 'Set Default Purse',
    blurb: 'Choose the default purse for reload-network loads (ReadyLink, Repower).',
    nodeLabel: 'DefaultPurse',
    fields: [
      SP,
      { id: 'pn', label: 'Default Purse Name', type: 'text', pattern: PURSE_NAME_RE, patmsg: '3 letters + 4 digits', help: 'Provide name OR slot.' },
      { id: 'slot', label: 'Default Purse Slot', type: 'number', help: 'Provide name OR slot.' },
    ],
    build: (v) => ({ node: 'DefaultPurse', rows: [
      ['PerformAction', '3'], ['Sub_ProgramName', v.sp], ['DefaultPurseSlot', v.slot], ['DefaultPurseName', v.pn],
    ] }),
  },
  {
    id: 'subprogram-add',
    group: 'Sub_Program & Purse',
    title: 'Add Sub_Program (core)',
    blurb: 'Create a VULTURE/HAWK subprogram — core identity, strategy & card fields.',
    nodeLabel: 'Sub_Program · Add (PA 1)',
    fields: [
      { id: 'prog', label: 'Program Name', type: 'text', required: true, maxLen: 30 },
      { id: 'sub', label: 'Sub_Program Name', type: 'text', required: true, maxLen: 30 },
      { id: 'report', label: 'Report Name', type: 'text', required: true, maxLen: 30 },
      { id: 'market', label: 'Market Segment', type: 'select', options: ['4 · Insurance', '3 · Incentive', '2 · Payroll', '7 · Gift'], default: '4 · Insurance' },
      { id: 'card', label: 'Card Type', type: 'select', options: ['1 · Normal', '2 · Emergency Relief', '13 · Gift'], default: '1 · Normal' },
      { id: 'authm', label: 'Authenticate Method', type: 'select', options: ['1 · Last 4 of SSN', '14 · PIN', '3 · CVX2', '9 · Pseudo'], default: '1 · Last 4 of SSN' },
      { id: 'strategy', label: 'Auth Strategy', type: 'select', options: ['VULTURE', 'HAWK'], default: 'HAWK', required: true },
      { id: 'iias', label: 'IIAS MCC Group', type: 'text', pattern: /^\d{5}$/, patmsg: '5-digit', required: (v) => v.strategy === 'HAWK', showIf: (v) => v.strategy === 'HAWK', help: 'Required for HAWK.' },
      { id: 'pkg', label: 'Package Name', type: 'text', required: true, maxLen: 80 },
      { id: 'bank', label: 'Issuing Bank', type: 'text', required: true, maxLen: 23 },
      { id: 'bin', label: 'BIN Number', type: 'text', required: true, pattern: /^\d{6}$/, patmsg: '6-digit BIN' },
      { id: 'active', label: 'Active', type: 'select', options: ['1', '0'], default: '1' },
    ],
    build: (v) => ({ node: 'Sub_Program', rows: [
      ['PerformAction', '1'], ['ProgramName', v.prog], ['Sub_ProgramName', v.sub], ['ReportName', v.report],
      ['MarketSegment', (v.market || '').split(' · ')[0]], ['CardType', (v.card || '').split(' · ')[0]],
      ['AuthenticateMethod', (v.authm || '').split(' · ')[0]], ['Active', v.active],
      ['PackageName', v.pkg], ['IssuingBank', v.bank], ['BinNumber', v.bin],
      ['IIASMCCGroup', v.strategy === 'HAWK' ? v.iias : ''], ['AuthStrategy', v.strategy],
    ] }),
  },
  {
    id: 'subprogram-update',
    group: 'Sub_Program & Purse',
    title: 'Update Sub_Program flags',
    blurb: 'Overwrite selected subprogram settings — only filled fields are emitted.',
    nodeLabel: 'Sub_Program · Overwrite (PA 3)',
    fields: [
      { id: 'sub', label: 'Sub_Program Name', type: 'text', required: true, maxLen: 30 },
      { id: 'newName', label: 'New Sub_Program Name', type: 'text', maxLen: 30 },
      { id: 'active', label: 'Active', type: 'select', options: ['', '1', '0'] },
      { id: 'strategy', label: 'Auth Strategy', type: 'select', options: ['', 'VULTURE', 'HAWK'] },
      { id: 'imm', label: 'Immediate Credit', type: 'select', options: ['', '0', '1', '2'], help: '0 off · 1 auth FS/APL · 2 settlement' },
      { id: 'deleg', label: 'Delegated Auth', type: 'select', options: ['', '1', '0'] },
      { id: 'autoRenew', label: 'Auto Renew', type: 'select', options: ['', '1', '0'] },
    ],
    build: (v) => ({ node: 'Sub_Program', rows: [
      ['PerformAction', '3'], ['Sub_ProgramName', v.sub], ['NewSub_ProgramName', v.newName],
      ['Active', v.active], ['AutoRenew', v.autoRenew], ['AuthStrategy', v.strategy],
      ['ImmediateCredit', v.imm], ['IsDelegatedAuthEnabled', v.deleg],
    ] }),
  },

  // ===================== Fees, MCCs & Countries =====================
  {
    id: 'fee',
    group: 'Fees, MCCs & Countries',
    title: 'Update Card Fee',
    blurb: 'Change the Replacement or Reissue fee on an existing subprogram.',
    nodeLabel: 'Configure_Fee (PA 2)',
    fields: [
      SP,
      { id: 'name', label: 'Fee', type: 'select', options: ['Card Replacement Fee', 'Card Reissue Fee'], default: 'Card Replacement Fee' },
      { id: 'amt', label: 'Fee Amount', type: 'number', required: true, min: 0, max: 19.99, help: 'Positive, under $20. Use 0 to remove.' },
    ],
    build: (v) => ({ node: 'Configure_Fee', rows: [
      ['PerformAction', '2'], ['Sub_ProgramName', v.sp], ['FeeName', v.name], ['FeeAmt', v.amt],
    ] }),
  },
  {
    id: 'highrisk',
    group: 'Fees, MCCs & Countries',
    title: 'High-Risk MCC',
    blurb: 'Decline (Add) or allow (Delete) a 4-digit MCC on a subprogram.',
    nodeLabel: 'Sub_Program › HighRiskMCCs',
    fields: [
      SP,
      { id: 'action', label: 'Action', type: 'select', options: ['Add', 'Delete'], default: 'Add' },
      { id: 'mcc', label: 'MCC', type: 'text', required: true, pattern: /^\d{4}$/, patmsg: 'Exactly 4 digits' },
    ],
    build: (v) => ({ node: 'Sub_Program', rows: [
      ['PerformAction', '3'], ['Sub_ProgramName', v.sp],
      { node: 'HighRiskMCCs', rows: [
        ['PerformAction', v.action === 'Delete' ? '3' : '1'], ['Sub_ProgramName', v.sp], ['Action', v.action], ['HighRiskMCC', v.mcc],
      ] },
    ] }),
  },
  {
    id: 'fudge',
    group: 'Fees, MCCs & Countries',
    title: 'Fudge MCC Tolerance',
    blurb: 'Allow a financial tolerance (amount or %) for a given MCC.',
    nodeLabel: 'Sub_Program › FudgeMCCs',
    fields: [
      SP,
      { id: 'action', label: 'Action', type: 'select', options: ['Add', 'Delete'], default: 'Add' },
      { id: 'mcc', label: 'Fudge MCC', type: 'text', required: true, pattern: /^\d{4}$/, patmsg: 'Exactly 4 digits' },
      { id: 'limit', label: 'Fudge Limit', type: 'number', required: true, min: 0, max: 99.99 },
      { id: 'type', label: 'Fudge Type', type: 'select', options: ['Amount', 'Percentage'], default: 'Amount' },
      { id: 'source', label: 'Fudge Source', type: 'select', options: ['', 'ITLVIS', 'MCV', 'MastercardDebit', 'DSV', 'EFD', 'CUP', 'Visa Base 1', 'Visa EU'] },
    ],
    build: (v) => ({ node: 'Sub_Program', rows: [
      ['PerformAction', '3'], ['Sub_ProgramName', v.sp],
      { node: 'FudgeMCCs', rows: [
        ['PerformAction', v.action === 'Delete' ? '3' : '1'], ['Sub_ProgramName', v.sp], ['Action', v.action],
        ['FudgeMCC', v.mcc], ['FudgeLimit', v.limit], ['FudgeType', v.type], ['FudgeSource', v.source],
      ] },
    ] }),
  },
  {
    id: 'mcc-group-members',
    group: 'Fees, MCCs & Countries',
    title: 'Add / Remove MCCs in a Group',
    blurb: 'Link or unlink MCCs on an existing MCC Group, by name or ID.',
    nodeLabel: 'MCCGroups › MCCGroup · Update (PA 3)',
    fields: [
      { id: 'gname', label: 'MCC Group Name', type: 'text', maxLen: 20, required: (v) => !String(v.gid).trim(), help: 'Provide name OR ID.' },
      { id: 'gid', label: 'MCC Group ID', type: 'text', pattern: /^\d{1,5}$/, patmsg: 'up to 5 digits', required: (v) => !String(v.gname).trim(), help: 'Provide name OR ID.' },
      { id: 'addList', label: 'MCCs to Add', type: 'text', maxLen: 250, pattern: /^\d+(\s*,\s*\d+)*$/, patmsg: 'Comma-separated MCC numbers, e.g. 7777,10002',
        required: (v) => !String(v.removeList).trim(), help: 'Comma-separated. Leave blank if only removing.' },
      { id: 'removeList', label: 'MCCs to Remove', type: 'text', maxLen: 250, pattern: /^\d+(\s*,\s*\d+)*$/, patmsg: 'Comma-separated MCC numbers, e.g. 666,10003',
        required: (v) => !String(v.addList).trim(), help: 'Comma-separated. Leave blank if only adding.' },
    ],
    build: (v) => {
      const clean = (s) => String(s || '').replace(/\s+/g, '');
      return { node: 'MCCGroups', rows: [{ node: 'MCCGroup', rows: [
        ['PerformAction', '3'], ['MCCGroupID', v.gid], ['MCCGroupName', v.gname],
        ['MCCAddList', clean(v.addList)], ['MCCRemoveList', clean(v.removeList)],
      ] }] };
    },
  },
  {
    id: 'country',
    group: 'Fees, MCCs & Countries',
    title: 'Block / Unblock Country',
    blurb: 'Authorize or block transactions by country code.',
    nodeLabel: 'BlockedCountries › BlockedCountry',
    fields: [
      SP,
      { id: 'mode', label: 'Mode', type: 'select', options: ['Block (1)', 'Unblock (3)'], default: 'Block (1)' },
      { id: 'code', label: 'Country Code', type: 'text', required: true, pattern: /^\d{1,3}$/, patmsg: '0–999' },
      { id: 'resp', label: 'Response', type: 'select', options: ['Service Restricted', 'Decline', 'Approval'], default: 'Service Restricted' },
    ],
    build: (v) => ({ node: 'BlockedCountries', rows: [{ node: 'BlockedCountry', rows: [
      ['PerformAction', v.mode.startsWith('Unblock') ? '3' : '1'], ['Sub_ProgramName', v.sp],
      ['CountryCode', v.code], ['ResponseDescription', v.resp],
    ] }] }),
  },
  {
    id: 'address-velocity',
    group: 'Fees, MCCs & Countries',
    title: 'Address Change Velocity',
    blurb: 'Configure freeze window and change limits for address updates.',
    nodeLabel: 'AddressChangeVelocity',
    fields: [
      SP,
      { id: 'mode', label: 'Mode', type: 'select', options: ['Add (1)', 'Remove (3)'], default: 'Add (1)' },
      { id: 'freeze', label: 'Freeze on Activation (days)', type: 'number', min: 0, max: 999, help: 'Default 30.' },
      { id: 'interval', label: 'Change Interval (days)', type: 'number', min: 0, max: 999, help: 'Default 30.' },
      { id: 'maxc', label: 'Max Changes / interval', type: 'number', min: 0, max: 999, help: 'Default 1.' },
      { id: 'cReplace', label: 'Confirm Replace Doc', type: 'select', options: ['', '1', '0'] },
      { id: 'cReissue', label: 'Confirm Reissue Doc', type: 'select', options: ['', '1', '0'] },
    ],
    build: (v) => ({ node: 'AddressChangeVelocity', rows: [
      ['PerformAction', v.mode.startsWith('Remove') ? '3' : '1'], ['Sub_ProgramName', v.sp],
      ['AddressFreezeOnActivation', v.freeze], ['AddressChangeInterval', v.interval], ['MaxAddressChanges', v.maxc],
      ['ConfirmReplaceDoc', v.cReplace], ['ConfirmReissueDoc', v.cReissue],
    ] }),
  },

  // ===================== Packages & BPA =====================
  {
    id: 'package',
    group: 'Packages & BPA',
    title: 'Create / Rename Package',
    blurb: 'Rename an existing package, or create a new one (create may need FIS).',
    nodeLabel: 'Package',
    fields: [
      { id: 'mode', label: 'Mode', type: 'select', options: ['Rename (2)', 'Create (1)'], default: 'Rename (2)' },
      { id: 'oldName', label: 'Old Package Name', type: 'text', maxLen: 80, required: (v) => v.mode.startsWith('Rename'), showIf: (v) => v.mode.startsWith('Rename') },
      { id: 'name', label: 'Package Name', type: 'text', required: true, maxLen: 80, help: 'New name (rename) / package name (create).' },
      { id: 'bank', label: 'Bank Name', type: 'text', maxLen: 23, required: (v) => v.mode.startsWith('Create'), showIf: (v) => v.mode.startsWith('Create') },
      { id: 'form', label: 'Form Factor', type: 'select', showIf: (v) => v.mode.startsWith('Create'),
        options: ['1 · Mag Stripe Visa/MC', '2 · Contact Chip + Mag', '8 · Mag Stripe Discover', '9 · MC Contactless + Mag'], default: '1 · Mag Stripe Visa/MC' },
      { id: 'art', label: 'Artwork Name', type: 'text', maxLen: 40, showIf: (v) => v.mode.startsWith('Create'), default: 'Zurich Benny Card' },
    ],
    build: (v) => ({ node: 'Package', rows: v.mode.startsWith('Create')
      ? [['PerformAction', '1'], ['PackageName', v.name], ['BankName', v.bank], ['FormFactorID', (v.form || '').split(' · ')[0]], ['ArtworkName', v.art]]
      : [['PerformAction', '2'], ['OldPackageName', v.oldName], ['PackageName', v.name]] }),
  },
  {
    id: 'sp-package-link',
    group: 'Packages & BPA',
    title: 'Link Package to Subprogram',
    blurb: 'Associate an existing package + BIN/PRIN to a subprogram.',
    nodeLabel: 'SubprogramPackageLinks › SubprogramPackageLink',
    fields: [
      SP,
      { id: 'pkg', label: 'Package Name', type: 'text', maxLen: 30, help: 'Provide name OR ID.' },
      { id: 'pkgId', label: 'Package ID', type: 'number', help: 'Provide name OR ID.' },
      { id: 'bin', label: 'BIN', type: 'text', required: true, pattern: /^\d{1,8}$/, patmsg: 'up to 8 digits' },
      { id: 'prin', label: 'PRIN', type: 'text', required: true, pattern: /^\d{1,3}$/, patmsg: '0–999' },
    ],
    build: (v) => ({ node: 'SubprogramPackageLinks', rows: [{ node: 'SubprogramPackageLink', rows: [
      ['PerformAction', '1'], ['Sub_ProgramName', v.sp], ['PackageName', v.pkg], ['PackageID', v.pkgId], ['BIN', v.bin], ['PRIN', v.prin],
    ] }] }),
  },
  {
    id: 'sp-package-bpa',
    group: 'Packages & BPA',
    title: 'Subprogram Package BPA Link',
    blurb: 'Bind a BIN/PRIN/Agent range to a package on a subprogram.',
    nodeLabel: 'SubprogramPackageBPALinks › SubprogramPackageBPALink',
    fields: [
      SP,
      { id: 'pkg', label: 'Package Name', type: 'text', maxLen: 30, help: 'Provide name OR ID.' },
      { id: 'pkgId', label: 'Package ID', type: 'number', help: 'Provide name OR ID.' },
      { id: 'bin', label: 'BIN', type: 'text', required: true, pattern: /^\d{1,8}$/, patmsg: 'up to 8 digits' },
      { id: 'prin', label: 'PRIN', type: 'text', required: true, pattern: /^\d{1,3}$/, patmsg: '0–999' },
      { id: 'begin', label: 'Begin Agent', type: 'number', required: true, min: 0, max: 99 },
      { id: 'end', label: 'End Agent', type: 'number', required: true, min: 0, max: 99 },
    ],
    build: (v) => ({ node: 'SubprogramPackageBPALinks', rows: [{ node: 'SubprogramPackageBPALink', rows: [
      ['PerformAction', '1'], ['Sub_ProgramName', v.sp], ['PackageName', v.pkg], ['PackageID', v.pkgId],
      ['BIN', v.bin], ['PRIN', v.prin], ['BeginAgent', v.begin], ['EndAgent', v.end],
    ] }] }),
  },
  {
    id: 'package-programs',
    group: 'Packages & BPA',
    title: 'Link Package (PackagePrograms)',
    blurb: 'Link a package to a subprogram with BIN and card-count agent ranges.',
    nodeLabel: 'PackagePrograms › PackageProgram',
    fields: [
      SP,
      { id: 'pkg', label: 'Package Name', type: 'text', maxLen: 30, help: 'Provide name OR ID.' },
      { id: 'pkgId', label: 'Package ID', type: 'number', help: 'Provide name OR ID.' },
      { id: 'bin', label: 'BIN Number', type: 'text', required: true, pattern: /^\d{1,6}$/, patmsg: 'up to 6 digits' },
      { id: 'total', label: 'Total Number of Cards', type: 'number', required: true, min: 1, max: 800000, help: 'Each 8,000 = a unique agent range.' },
      { id: 'prin', label: 'PRIN Number', type: 'text', pattern: /^\d{1,3}$/, patmsg: '0–999', help: 'Optional — next available if blank.' },
    ],
    build: (v) => ({ node: 'PackagePrograms', rows: [{ node: 'PackageProgram', rows: [
      ['PerformAction', '1'], ['Sub_ProgramName', v.sp], ['PackageName', v.pkg], ['PackageID', v.pkgId],
      ['BinNumber', v.bin], ['TotalNumberOfCards', v.total], ['PRINNumber', v.prin],
    ] }] }),
  },
];

export function getRecipe(id) {
  return RECIPES.find((r) => r.id === id);
}
